import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { getErrorResponseMessage } from "@via/utils/helpers.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  TrustedGitRepository,
  TrustedGitRepositoryListResponse,
  TrustGitRepositoryRequestBody,
} from "common";

const TRUSTED_REPOSITORIES_URL = "/api/admin/git/trusted-repositories";

export const useTrustedRepositories = (): UseQueryResult<
  TrustedGitRepositoryListResponse,
  Error
> =>
  useQuery<TrustedGitRepositoryListResponse, Error>({
    queryKey: [TRUSTED_REPOSITORIES_URL],
    queryFn: async () => (await axios.get(TRUSTED_REPOSITORIES_URL)).data,
    retry: false,
  });

export const useTrustRepository = (): UseMutationResult<
  TrustedGitRepository,
  ErrorNotification,
  TrustGitRepositoryRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    TrustedGitRepository,
    ErrorNotification,
    TrustGitRepositoryRequestBody
  >({
    mutationFn: async body =>
      (await axios.post(TRUSTED_REPOSITORIES_URL, body)).data,
    onSuccess: row => {
      setNotification({
        id: "trust-repository",
        message: "Repository trusted",
        details: row.repoUrl,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [TRUSTED_REPOSITORIES_URL],
        refetchType: "all",
      });
    },
    onError: error =>
      setNotification({
        id: "trust-repository",
        message: "Could not trust the repository",
        details: getErrorResponseMessage(error) ?? "Please try again.",
        variant: NOTIFICATION.ERROR,
      }),
  });
};

export const useRevokeTrustedRepository = (): UseMutationResult<
  TrustedGitRepository,
  ErrorNotification,
  number
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<TrustedGitRepository, ErrorNotification, number>({
    mutationFn: async trustId =>
      (await axios.post(`${TRUSTED_REPOSITORIES_URL}/${trustId}/revoke`)).data,
    onSuccess: row => {
      setNotification({
        id: "revoke-repository",
        message: "Trust revoked",
        details: row.repoUrl,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [TRUSTED_REPOSITORIES_URL],
        refetchType: "all",
      });
    },
    onError: error =>
      setNotification({
        id: "revoke-repository",
        message: "Could not revoke trust",
        details: getErrorResponseMessage(error) ?? "Please try again.",
        variant: NOTIFICATION.ERROR,
      }),
  });
};
